import type { PartnerBoxMapContent } from '$lib/components/advertising/partners/types';
import type { LayoutLoad } from './$types';
import { AdvertisingPartnerType, GetPartnersBoxMapsStore, GetZoisStore, SearchItineraryStore } from '$houdini';
import { getWaypointCoordinates } from '$lib/components/search/itinerary/ItineraryStore';
import { createPartnerBoxmapsStore } from '$lib/services/store/partnerBoxmaps';
import { augmentHoudiniStore } from '$lib/utils/augmentedHoudiniStore';
import { convertDistanceUnitsToMeters } from '$lib/utils/distance';
import { filterUndefined } from '$lib/utils/predicates';
import { getItineraryResultsStore } from '$routes/(map)/routes/loadItinerary';
import { getWaypointsInfo } from '$routes/(map)/routes/waypoints';
import { loadZois } from './loadZois';

export const load = (async (event) => {
	const { searchParams } = await event.parent();
	const searchItineraryStore = augmentHoudiniStore(new SearchItineraryStore());
	const searchZoisStore = augmentHoudiniStore(new GetZoisStore());
	const getPartnersBoxMapsStore = augmentHoudiniStore(new GetPartnersBoxMapsStore());

	const itineraryResultsStore = getItineraryResultsStore({
		searchItineraryStore,
	});

	const boxmapsForItineraryStore = createPartnerBoxmapsStore();

	itineraryResultsStore.onSuccess(async ($itineraryResultsStore) => {
		const waypointsInfo = await getWaypointsInfo(event, searchParams);
		const itineraryResult = $itineraryResultsStore.data?.searchItinerary;
		if ((itineraryResult && itineraryResult.__typename === 'SearchItineraryNotFoundResult') || !waypointsInfo) {
			return;
		}

		const { waypoints: urlWaypoints, departure, arrival } = waypointsInfo;
		const coordinates = await Promise.all(urlWaypoints.map(getWaypointCoordinates));

		const getPartnersBoxMapsInput = {
			departureName: departure.name,
			arrivalName: arrival.name,
			distance: convertDistanceUnitsToMeters(itineraryResult?.routes?.at(0)?.routeDistance) ?? undefined,
			coordinates: coordinates.filter(filterUndefined),
		};

		if (getPartnersBoxMapsInput.coordinates.length) {
			const $getPartnersBoxMapsStore = await getPartnersBoxMapsStore.fetch({ event, policy: 'NetworkOnly', variables: { input: getPartnersBoxMapsInput } }).catch(() => {
				console.error('Error fetching partners box maps');
			});
			if (!$getPartnersBoxMapsStore) {
				return;
			}
			const partnersBoxMaps = $getPartnersBoxMapsStore.data?.getPartnersBoxMaps;

			if (!partnersBoxMaps || partnersBoxMaps.__typename !== 'GetPartnersBoxMapsResultSuccess') {
				return [];
			}

			const partnersBoxMapsForItinerary = partnersBoxMaps.boxMaps.filter(
				(item): item is PartnerBoxMapContent =>
					!!item && !('errors' in item),
			).map(item => item.type === AdvertisingPartnerType.BOOKING
				? {
						type: AdvertisingPartnerType.BOOKING,
						context: 'itinerary_result_layout' as const,
					}
				: item);

			boxmapsForItineraryStore.set(partnersBoxMapsForItinerary);
		}
	});

	return {
		searchItineraryStore,
		itineraryResultsStore,
		searchZoisStore,
		zoiResultsStore: loadZois(searchZoisStore, itineraryResultsStore),
		boxmapsForItineraryStore,
	};
}) satisfies LayoutLoad;
